/**
 * Card a template view renders itself into (title, optional description, a
 * compact body) with an "enlarge" affordance that opens the same body full
 * size in a dialog. Shared by `ProposalView`, `ChangeView` and
 * `WizardPreviewView`.
 */
import {
  Close as CloseIcon,
  OpenInFull as OpenInFullIcon,
} from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";

export function TemplateViewFrame({
  title,
  description,
  renderBody,
}: {
  title: string;
  description: string | null;
  renderBody: (compact: boolean) => React.ReactNode;
}) {
  const { t } = useTranslate(["Assistant", "Global"]);
  const [open, setOpen] = React.useState(false);

  return (
    <Paper variant="outlined" sx={{ p: 1, mt: 1 }}>
      <Stack direction="row" sx={{ alignItems: "center", mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {title}
        </Typography>
        <Tooltip title={t("Enlarge view", { ns: "Assistant" })}>
          <IconButton
            size="small"
            aria-label={t("Enlarge view", { ns: "Assistant" })}
            onClick={() => setOpen(true)}
          >
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      {description && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mb: 0.5 }}
        >
          {description}
        </Typography>
      )}
      {renderBody(true)}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="lg"
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>{title}</Box>
          <IconButton
            aria-label={t("Close", { ns: "Global" })}
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {description}
            </Typography>
          )}
          {renderBody(false)}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
